(() => {
  const instances = new WeakMap();
  const speed = 22; // Pixels per second, independent of main-thread frame timing.

  function setup(root) {
    if (instances.has(root)) return;
    const track = root.querySelector('.gx-feedback__track');
    const viewport = root.querySelector('.gx-feedback__viewport');
    if (!track || !viewport) return;
    const cards = [...track.children].filter(card => !card.hasAttribute('data-gx-feedback-clone'));
    if (cards.length < 2 || typeof track.animate !== 'function') return;
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const events = new AbortController();
    const options = { signal: events.signal };
    const clone = card => {
      const copy = card.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      copy.setAttribute('inert', '');
      copy.removeAttribute('aria-label');
      copy.dataset.gxFeedbackClone = 'true';
      return copy;
    };
    track.prepend(...cards.map(clone));
    track.append(...cards.map(clone));
    root.classList.add('gx-feedback--ready');
    root.dataset.gxCarouselReady = 'true';
    root.dataset.gxFeedbackMotion = 'compositor';

    let animation, duration = 0, step = 0, visible = true, paused = false, drag = null;
    const modulo = (value, modulus) => ((value % modulus) + modulus) % modulus;
    const phase = () => duration ? modulo(Number(animation?.currentTime) || 0, duration) / duration : 0;
    const syncPlayback = () => {
      if (!animation) return;
      const stopped = paused || drag !== null || motion.matches || document.hidden || !visible;
      if (stopped && animation.playState !== 'paused') animation.pause();
      else if (!stopped && animation.playState !== 'running') animation.play();
      root.dataset.gxFeedbackPaused = String(paused || motion.matches);
    };
    const resize = () => {
      const nextStep = cards[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
      if (nextStep <= 0 || (animation && Math.abs(nextStep - step) < 0.01)) return;
      const progress = phase();
      step = nextStep;
      const cycle = step * cards.length;
      duration = cycle / speed * 1000;
      animation?.cancel();
      // Both ends show identical copies. The compositor loops without a JS reset frame.
      animation = track.animate([
        { transform: `translate3d(${-cycle}px,0,0)` },
        { transform: `translate3d(${-cycle * 2}px,0,0)` },
      ], { duration, iterations: Infinity, easing: 'linear' });
      animation.currentTime = progress * duration;
      syncPlayback();
    };
    const seek = pixels => {
      if (!animation || !duration) return;
      animation.currentTime = modulo((Number(animation.currentTime) || 0) + pixels / speed * 1000, duration);
    };
    viewport.addEventListener('pointerdown', event => {
      if (event.button !== 0 || event.isPrimary === false) return;
      drag = { id: event.pointerId, startX: event.clientX, lastX: event.clientX, moved: false };
      syncPlayback();
      viewport.setPointerCapture(event.pointerId);
      root.classList.add('gx-feedback--dragging');
    }, options);
    viewport.addEventListener('pointermove', event => {
      if (!drag || event.pointerId !== drag.id) return;
      if (Math.abs(event.clientX - drag.startX) > 4) drag.moved = true;
      if (drag.moved) {
        seek(drag.lastX - event.clientX);
        event.preventDefault();
      }
      drag.lastX = event.clientX;
    }, options);
    const finishDrag = event => {
      if (!drag || (event && event.pointerId !== drag.id)) return;
      const id = drag.id;
      drag = null;
      root.classList.remove('gx-feedback--dragging');
      if (viewport.hasPointerCapture(id)) viewport.releasePointerCapture(id);
      syncPlayback();
    };
    viewport.addEventListener('pointerup', finishDrag, options);
    viewport.addEventListener('pointercancel', finishDrag, options);
    viewport.addEventListener('lostpointercapture', () => finishDrag(), options);
    window.addEventListener('blur', () => finishDrag(), options);
    viewport.addEventListener('keydown', event => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        seek(event.key === 'ArrowRight' ? step : -step);
      } else if (event.code === 'Space') {
        event.preventDefault();
        paused = !paused;
        syncPlayback();
      }
    }, options);
    // Hover and pointer focus never stop the belt; only an active drag/manual pause does.
    document.addEventListener('visibilitychange', syncPlayback, options);
    window.addEventListener('resize', resize, options);
    window.addEventListener('pageshow', () => { resize(); syncPlayback(); }, options);
    motion.addEventListener('change', syncPlayback, options);
    const observer = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      syncPlayback();
    }, { threshold: 0 });
    observer.observe(viewport);
    const sizeObserver = new ResizeObserver(resize);
    sizeObserver.observe(viewport);
    resize();
    instances.set(root, () => {
      events.abort();
      animation?.cancel();
      observer.disconnect();
      sizeObserver.disconnect();
      track.querySelectorAll('[data-gx-feedback-clone]').forEach(card => card.remove());
      root.classList.remove('gx-feedback--ready', 'gx-feedback--dragging');
      delete root.dataset.gxCarouselReady;
      instances.delete(root);
    });
  }
  const mount = scope => scope.querySelectorAll('[data-gx-feedback-carousel]').forEach(setup);
  mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-gx-feedback-carousel]').forEach(root => instances.get(root)?.());
  });
})();
